import { useCallback, useMemo, useState } from 'react';

import { formatDate } from '../../utils/formatters';

import { ColumnDefinition } from '../../features/table-preferences/types';

import { DateField } from '../../components/DateField/DateField';

import { OrderActionsDropdown } from '../../components/OrderActionsDropdown/OrderActionsDropdown';

import {
  useApproveOrgItem,
  useUpdateOrgItemStatus,
  useUpdateOrgOrderDate,
} from '../../features/org-orders/api';

// Column rules — pure, unit-tested, no React.

// See features/org-orders/orderColumns.

import {
  STATUS_OPTIONS,
  canChangeStatus,
  carrierRateCell,
  carrierTooltip,
  orderDateOf,
  planStatusChange,
  statusSelectTitle,
  toDateInputValue,
} from '../../features/org-orders/orderColumns';

import type { OrgOrderItem } from '../../features/org-orders/types';
import { describeOrderActionError } from '../../features/org-orders/actionErrors';

import { toast } from 'react-toastify';

import './useOrgOrderColumns.scss';

import { NavLink } from 'react-router-dom';

import { useFuelSurcharge, pickSurchargeForDate } from '../../features/fuel-surcharge/api';

import { formatCents, formatPct } from '../../features/pricing/money';

/** Re-exported for the call sites that imported it from here. */

export type { OrgOrderItem };

interface UseOrgOrderColumnsProps {
  onViewNotes: (item: OrgOrderItem) => void;

  onManage: (item: OrgOrderItem | { id: string }) => void;

  onSendBOL: (item: OrgOrderItem) => void;
}

function formatBlumeMoney(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',

    currency: 'USD',

    maximumFractionDigits: 0,
  }).format(value);
}

function orderCreatorRoleLabel(role?: string | null): string {
  if (role === 'ORG_ADMIN') return 'Org Admin';
  if (role === 'ORG_OWNER') return 'Org Owner';
  if (role === 'CUSTOMER') return 'Customer';
  if (role === 'SUBUSER') return 'Customer';

  return role?.replace(/_/g, ' ') || 'Unknown';
}

function renderOrderCreator(row: any) {
  const creator = row?.createdBy;

  if (!creator) {
    return <span className="muted">—</span>;
  }

  const name = creator.actorName?.trim();
  const email = creator.actorEmail?.trim();
  const role = orderCreatorRoleLabel(creator.actorRole);
  const primary = name || email || 'Unknown';
  const secondary = name && email ? `${email} · ${role}` : role;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: 0,
      }}
      title={name && email ? `${name} (${email}) — ${role}` : `${primary} — ${role}`}
    >
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {primary}
      </span>

      <span
        className="muted"
        style={{
          fontSize: '12px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {secondary}
      </span>
    </div>
  );
}

export function useOrgOrderColumns({ onViewNotes, onManage, onSendBOL }: UseOrgOrderColumnsProps) {
  const updateStatus = useUpdateOrgItemStatus();

  const approveItem = useApproveOrgItem();

  const updateOrderDate = useUpdateOrgOrderDate();

  const { data: fuelSurchargeWeeks = [] } = useFuelSurcharge();

  const surchargeWeekForRow = useCallback(
    (row: any) => {
      const weeks = Array.isArray(fuelSurchargeWeeks) ? fuelSurchargeWeeks : [];

      if (weeks.length === 0) {
        return null;
      }

      /*

       * Same rule as the manage-load pricing estimate:

       * use the delivery date when one exists; otherwise use the current/latest

       * published fuel-surcharge week.

       *

       * Table rows can carry a full ISO timestamp, while pickSurchargeForDate()

       * expects YYYY-MM-DD.

       */

      const deliveryDateISO = row?.deliveryDate ? String(row.deliveryDate).slice(0, 10) : '';

      const match = deliveryDateISO ? pickSurchargeForDate(weeks, deliveryDateISO) : null;

      return (
        match?.week ?? weeks.find((week: any) => week.isCurrent) ?? weeks[weeks.length - 1] ?? null
      );
    },

    [fuelSurchargeWeeks]
  );

  const stonePriceCentsForRow = useCallback((row: any): number | null => {
    const pricing = row?.pricing;

    const warehouseId = row?.location?.id ?? row?.locationId ?? null;

    if (!warehouseId || !pricing?.stonePriceByLocation) {
      return null;
    }

    const value = pricing.stonePriceByLocation[warehouseId];

    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }, []);

  const [editingDateFor, setEditingDateFor] = useState<string | null>(null);

  const [draftOrderDate, setDraftOrderDate] = useState<string>('');

  const renderDateCell = useCallback(
    (row: any) => {
      const dt = orderDateOf(row);

      const isEditing = editingDateFor === row.id;

      const handleStartEdit = () => {
        setEditingDateFor(row.id);

        setDraftOrderDate(toDateInputValue(dt));
      };

      const handleCancelEdit = () => {
        setEditingDateFor(null);

        setDraftOrderDate('');
      };

      const handleSaveDate = async () => {
        if (!draftOrderDate) {
          toast.error('Please select a date');

          return;
        }

        try {
          await updateOrderDate.mutateAsync({
            itemId: row.id,

            newDate: draftOrderDate,
          });

          toast.success('Order date updated');

          setEditingDateFor(null);

          setDraftOrderDate('');
        } catch (e: any) {
          toast.error(e?.response?.data?.error || 'Failed to update order date');
        }
      };

      if (isEditing) {
        return (
          <div className="order-date-edit">
            <DateField
              value={draftOrderDate}
              onChange={setDraftOrderDate}
              className="order-date-edit__input"
            />

            <div className="order-date-edit__actions">
              <button
                type="button"
                className="btn btn--ghost btn--xs"
                onClick={handleCancelEdit}
                disabled={updateOrderDate.isPending}
              >
                Cancel
              </button>

              <button
                type="button"
                className="btn btn--primary btn--xs"
                onClick={handleSaveDate}
                disabled={updateOrderDate.isPending || !draftOrderDate}
              >
                {updateOrderDate.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="order-date-cell">
          <span>{dt ? formatDate(dt) : '—'}</span>

          <button type="button" className="link-btn" onClick={handleStartEdit}>
            Edit
          </button>
        </div>
      );
    },

    [editingDateFor, draftOrderDate, updateOrderDate]
  );

  const columns: ColumnDefinition<OrgOrderItem>[] = useMemo(
    () => [
      {
        id: 'load-number',

        description: 'Unique number for this load. Click to open the manage dialog.',

        header: 'Load #',

        visible: true,

        defaultOrder: 0,

        sortable: true,

        sortKey: 'load-number',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => {
          const load = row.loadNumber || '—';

          return (
            <button
              type="button"
              className="btn btn--link"
              onClick={() => onManage(row)}
              style={{ padding: 0, border: 'none', background: 'none' }}
              title="Manage Order"
            >
              {load}
            </button>
          );
        },
      },

      {
        id: 'tags',

        description: 'Internal order tags such as ASAP, look tomorrow, or check received.',

        header: 'Tags',

        visible: true,

        defaultOrder: 0.6,

        sortable: false,

        defaultWidth: 220,

        minWidth: 160,

        maxWidth: 360,

        render: (row) => {
          const tags = Array.isArray(row.order?.tags) ? row.order.tags.filter(Boolean) : [];

          if (!tags.length) {
            return <span className="muted">—</span>;
          }

          const MAX_VISIBLE = 2;

          const visibleTags = tags.slice(0, MAX_VISIBLE);

          const hiddenCount = tags.length - visibleTags.length;

          return (
            <div className="order-table-tags" title={tags.join(', ')}>
              {visibleTags.map((tag: string) => (
                <span key={tag} className="order-table-tag">
                  {tag}
                </span>
              ))}

              {hiddenCount > 0 && (
                <span className="order-table-tag order-table-tag--more">+{hiddenCount}</span>
              )}
            </div>
          );
        },
      },

      {
        id: 'customer',

        description:
          'Customer the order was placed for. A red $ means they must prepay before the load ships.',

        header: 'Customer',

        visible: true,

        defaultOrder: 1,

        sortable: true,

        sortKey: 'customer',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => {
          const customerId = row.cust?.id ?? null;

          const customerName = row.cust?.name || '—';

          // The sub-location this load ships to — its "Location Name" (label),

          // falling back to the street so the row still identifies the location.

          const address = row.order?.deliveryAddress;

          const locationName = address?.label || address?.line1 || null;

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',

                  alignItems: 'center',

                  gap: 6,

                  minWidth: 0,
                }}
              >
                {row.cust?.requiresPrepay && (
                  <span
                    style={{
                      color: '#dc2626',

                      fontSize: '16px',

                      fontWeight: 'bold',

                      flexShrink: 0,
                    }}
                    title="Requires prepayment"
                  >
                    $
                  </span>
                )}

                {customerId ? (
                  <NavLink
                    to={`/customers?customerId=${encodeURIComponent(customerId)}`}
                    className="table-cell-link"
                    title={`View ${customerName}`}
                  >
                    {customerName}
                  </NavLink>
                ) : (
                  <span>{customerName}</span>
                )}
              </div>

              {locationName && (
                <span
                  className="muted"
                  style={{
                    fontSize: '12px',

                    overflow: 'hidden',

                    textOverflow: 'ellipsis',

                    whiteSpace: 'nowrap',
                  }}
                  title={locationName}
                >
                  {locationName}
                </span>
              )}
            </div>
          );
        },
      },

      {
        id: 'created-by',
        description: 'Who originally created this order, read from the ORDER CREATE audit log.',
        header: 'Created By',
        visible: true,
        defaultOrder: 1.1,
        sortable: false,
        defaultWidth: 220,
        minWidth: 170,
        maxWidth: 360,
        render: (row: any) => renderOrderCreator(row),
      },

      {
        id: 'credit-limit',
        description: 'Customer credit limit synchronized from Blume.',
        header: 'Credit Limit',
        visible: true,
        defaultOrder: 1.15,
        sortable: true,
        sortKey: 'creditLimit',
        defaultWidth: 140,
        minWidth: 120,
        maxWidth: 240,
        render: (row) => {
          const value = row.order?.customer?.blumeCreditLimit;
          return value == null ? (
            <span className="muted">—</span>
          ) : (
            <strong>{formatBlumeMoney(value)}</strong>
          );
        },
      },

      {
        id: 'available-credit',

        description: 'Available customer credit synchronized from Blume.',

        header: 'Available Credit',

        visible: true,

        defaultOrder: 1.2,

        sortable: true,

        sortKey: 'availableCredit',

        defaultWidth: 140,

        minWidth: 120,

        maxWidth: 240,

        render: (row) => {
          const value = row.order?.customer?.blumeCreditAvailable;

          if (value === null || value === undefined) {
            return <span className="muted">—</span>;
          }

          return <strong>{formatBlumeMoney(value)}</strong>;
        },
      },

      {
        id: 'overdue-balance',

        description: 'Customer overdue invoice balance synchronized from Blume.',

        header: 'Overdue Balance',

        visible: true,

        defaultOrder: 1.3,

        sortable: true,

        sortKey: 'overdueBalance',

        defaultWidth: 140,

        minWidth: 120,

        maxWidth: 240,

        render: (row) => {
          const value = row.order?.customer?.blumeOverdueInvoiceBalance;

          if (value === null || value === undefined) {
            return <span className="muted">—</span>;
          }

          return (
            <strong
              style={{
                color: value > 0 ? '#b42318' : undefined,
              }}
            >
              {formatBlumeMoney(value)}
            </strong>
          );
        },
      },

      {
        id: 'sales-rep',

        description: "Org member assigned to this load's delivery location.",

        header: 'Sales Rep',

        visible: true,

        defaultOrder: 1.5,

        sortable: true,

        sortKey: 'sales-rep',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        // Reps are assigned per delivery location only.

        render: (row) => row.order?.deliveryAddress?.salesRep?.email || '—',
      },

      {
        id: 'tons',

        description: 'Weight of the load in tons, calculated from the product and quantity.',

        header: 'Tons',

        visible: true,

        defaultOrder: 1.6,

        sortable: true,

        sortKey: 'tons',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => {
          return <span>{row.quantity}</span>;
        },
      },

      {
        id: 'ticket-number',

        description: 'Ticket number for this load, if the carrier has provided one.',

        header: 'Ticket #',

        visible: true,

        defaultOrder: 1.7,

        sortable: true,

        sortKey: 'ticket-number',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => row.ticketNumber || '—',
      },

      {
        id: 'po-number',

        description: "The customer's purchase order number for this load, if they supplied one.",

        header: 'PO Number',

        visible: true,

        defaultOrder: 0.5,

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => row.order?.poNumber || '—',
      },

      {
        id: 'date',

        description: 'Date the order was placed. Click Edit to correct it.',

        header: 'Order Date',

        visible: true,

        defaultOrder: 2,

        sortable: true,

        sortKey: 'date',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => renderDateCell(row),
      },

      {
        id: 'status',

        description:
          'Where this load is in its lifecycle. Change it here, or in the manage dialog for bigger edits.',

        header: 'Status',

        visible: true,

        defaultOrder: 3,

        sortable: true,

        sortKey: 'status',

        defaultWidth: 180,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => {
          const hasNotes = !!(row.cancellationReason || row.rejectionReason);

          const statusEditable = canChangeStatus(row);

          const handleChangeStatus = async (newStatus: string) => {
            // Which endpoint this belongs to, and whether it can go at all.

            const plan = planStatusChange(row, newStatus);

            if (plan.kind === 'ignore') {
              return;
            }

            if (plan.kind === 'blocked') {
              toast.error(plan.message);

              return;
            }

            try {
              if (plan.kind === 'approve') {
                await approveItem.mutateAsync({
                  orderId: row.orderId!,

                  itemId: row.id,

                  ...plan.payload,
                });

                toast.success('Load approved');

                return;
              }

              await updateStatus.mutateAsync({
                orderId: row.orderId!,

                itemId: row.id,

                status: plan.status,
              });

              toast.success('Status updated successfully');
            } catch (e: any) {
              // `mutate` used to be fire-and-forget here, so the surrounding

              // try/catch could never see a rejection and every attempt —

              // including the ones the API refused — reported success.

              toast.error(
                describeOrderActionError(e?.response?.data?.error, 'Failed to update status')
              );
            }
          };

          return (
            <div>
              <select
                className={`status-select status-select--${(row.status || 'unknown').toLowerCase()}`}
                value={row.status}
                onChange={(e) => handleChangeStatus(e.target.value)}
                disabled={!statusEditable}
                title={statusSelectTitle(row)}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              {hasNotes && (
                <button type="button" className="note-chip" onClick={() => onViewNotes(row)}>
                  <span className="note-dot" aria-hidden="true" />
                  View notes
                </button>
              )}
            </div>
          );
        },
      },

      {
        id: 'carrier',

        description:
          'Trucking company assigned to haul this load. Assign one in the manage dialog.',

        header: 'Carrier',

        visible: true,

        defaultOrder: 4,

        sortable: true,

        sortKey: 'carrier',

        // The cell only has room for the company name; surface the dispatch

        // details staff would otherwise open the manage dialog to read.

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        tooltip: (row) => carrierTooltip(row),

        render: (row) => {
          return row.carrier?.companyName || <span className="muted">—</span>;
        },
      },

      {
        id: 'driver',

        description: "Driver or dispatcher assigned from the carrier's contacts.",

        header: 'Driver',

        visible: false,

        defaultOrder: 4.5,

        sortable: true,

        sortKey: 'driver',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => row.driverName || <span className="muted">—</span>,
      },

      {
        id: 'truck-number',

        description: "Truck running this load, from the carrier's truck list.",

        header: 'Truck #',

        visible: false,

        defaultOrder: 4.6,

        sortable: true,

        sortKey: 'truckNumber',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => row.truckNumber || <span className="muted">—</span>,
      },

      {
        id: 'product',

        description: 'Material being shipped.',

        header: 'Product',

        visible: true,

        defaultOrder: 5,

        sortable: true,

        sortKey: 'product',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => {
          return (
            <div className="prod-col">
              <div className="prod-col__text">
                <div className="prod-col__name">{row.product?.name || '—'}</div>
              </div>
            </div>
          );
        },
      },

      {
        id: 'fulfillment-method',

        description: 'How the load ships. This is the fulfilment method chosen at approval.',

        header: 'Fulfillment Method',

        visible: false,

        defaultOrder: 5.5,

        sortable: true,

        sortKey: 'fulfillmentMethod',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => row.orgLoadOptionNameSnapshot || <span className="muted">—</span>,
      },

      {
        id: 'pickup-date',

        description: 'Date the carrier is scheduled to collect the load from the warehouse.',

        header: 'Pickup Date',

        visible: false,

        defaultOrder: 6,

        sortable: true,

        sortKey: 'pickupDate',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => {
          if (!row.pickupDate) return <span className="muted">—</span>;

          return formatDate(row.pickupDate);
        },
      },

      {
        id: 'delivery-date',

        description: 'Date the load is scheduled to reach the customer.',

        header: 'Delivery Date',

        visible: false,

        defaultOrder: 7,

        sortable: true,

        sortKey: 'deliveryDate',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => {
          if (!row.deliveryDate) return <span className="muted">—</span>;

          return formatDate(row.deliveryDate);
        },
      },

      {
        id: 'carrier-rate',

        description:
          'Freight charged to the customer for this load, from the rate set for their delivery address and warehouse pair.',

        header: 'Carrier Rate',

        className: 'ta-right',

        visible: false,

        defaultOrder: 8,

        sortable: true,

        sortKey: 'carrierRate',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => carrierRateCell(row) ?? <span className="muted">—</span>,
      },

      {
        id: 'delivery-pct',

        description:
          'Fuel surcharge percentage applied to the delivery portion of pricing for this load.',

        header: 'Delivery %',

        className: 'ta-right',

        visible: false,

        defaultOrder: 8.1,

        sortable: false,

        defaultWidth: 120,

        minWidth: 100,

        maxWidth: 220,

        render: (row) => {
          const week = surchargeWeekForRow(row);

          return week ? formatPct(week.deliveryPct) : <span className="muted">—</span>;
        },
      },

      {
        id: 'material-pct',

        description:
          'Fuel surcharge percentage applied to the material portion of pricing for this load.',

        header: 'Material %',

        className: 'ta-right',

        visible: false,

        defaultOrder: 8.2,

        sortable: false,

        defaultWidth: 120,

        minWidth: 100,

        maxWidth: 220,

        render: (row) => {
          const week = surchargeWeekForRow(row);

          return week ? formatPct(week.materialPct) : <span className="muted">—</span>;
        },
      },

      {
        id: 'stone-price',

        description:
          'Stone/material price per ton at the warehouse currently fulfilling this load.',

        header: 'Stone Price / Ton',

        className: 'ta-right',

        visible: false,

        defaultOrder: 8.3,

        sortable: false,

        defaultWidth: 140,

        minWidth: 120,

        maxWidth: 240,

        render: (row) => {
          const stonePriceCents = stonePriceCentsForRow(row);

          return stonePriceCents == null ? (
            <span className="muted">—</span>
          ) : (
            formatCents(stonePriceCents)
          );
        },
      },

      {
        id: 'pickup-location',

        description: 'Warehouse fulfilling this load.',

        header: 'Warehouse',

        visible: false,

        defaultOrder: 9,

        sortable: true,

        sortKey: 'pickupLocation',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        // Warehouse names alone ("Yard 2") don't tell you where to send a truck.

        tooltip: (row) => {
          const loc = row.location;

          if (!loc?.name) return null;

          const address = [loc.line1, [loc.city, loc.state].filter(Boolean).join(', ')]

            .filter(Boolean)

            .join(' · ');

          return address ? `${loc.name} · ${address}` : loc.name;
        },

        render: (row) => {
          const loc = row.location;

          if (!loc || !loc.name) return <span className="muted">—</span>;

          return <div>{loc.name}</div>;
        },
      },

      {
        id: 'pickup-state',

        description: 'State of the warehouse the load ships from.',

        header: 'Pickup State',

        visible: false,

        defaultOrder: 9,

        sortable: true,

        sortKey: 'pickupState',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => {
          const loc = row.location;

          if (!loc || !loc.state) return <span className="muted">—</span>;

          return <div>{loc.state}</div>;
        },
      },

      {
        id: 'pickup-city',

        description: 'City of the warehouse the load ships from.',

        header: 'Pickup City',

        visible: false,

        defaultOrder: 9.1,

        sortable: true,

        sortKey: 'pickupCity',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => {
          const loc = row.location;

          if (!loc || !loc.city) return <span className="muted">—</span>;

          return <div>{loc.city}</div>;
        },
      },

      {
        id: 'delivery-state',

        description: 'State the load is being delivered to.',

        header: 'Delivery State',

        visible: false,

        defaultOrder: 9,

        sortable: true,

        sortKey: 'deliveryState',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => {
          const loc = row.order?.deliveryAddress;

          if (!loc || !loc.state) return <span className="muted">—</span>;

          return (
            <div>
              <div>{loc.state}</div>
            </div>
          );
        },
      },

      {
        id: 'delivery-city',

        description: 'City the load is being delivered to.',

        header: 'Delivery City',

        visible: false,

        defaultOrder: 9,

        sortable: true,

        sortKey: 'deliveryCity',

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => {
          const loc = row.order?.deliveryAddress;

          if (!loc || !loc.city) return <span className="muted">—</span>;

          return (
            <div>
              <div>{loc.city}</div>
            </div>
          );
        },
      },

      {
        id: 'resale-cert',

        description: 'Whether this customer has a resale certificate on file.',

        header: 'Resale Cert',

        visible: false,

        defaultOrder: 10,

        sortable: false,

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => {
          const doc = row.cust?.documents?.find((d) => d.type === 'RESALE_CERTIFICATE');

          if (!doc) return <span className="doc-status doc-status--no">No</span>;

          if (doc.status === 'REJECTED')
            return <span className="doc-status doc-status--rejected">Rejected</span>;

          return <span className="doc-status doc-status--yes">Yes</span>;
        },
      },

      {
        id: 'credit-app',

        description: 'Whether this customer has a signed credit application / agreement on file.',

        header: 'Credit App',

        visible: false,

        defaultOrder: 11,

        sortable: false,

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        render: (row) => {
          const doc = row.cust?.documents?.find((d) => d.type === 'AGREEMENT');

          if (!doc) return <span className="doc-status doc-status--no">No</span>;

          if (doc.status === 'REJECTED')
            return <span className="doc-status doc-status--rejected">Rejected</span>;

          return <span className="doc-status doc-status--yes">Yes</span>;
        },
      },

      {
        id: 'actions',

        description: 'Per-load actions: manage, view notes, send the BOL.',

        header: 'Actions',

        visible: true,

        defaultOrder: 100,

        defaultWidth: 120,

        minWidth: 120,

        maxWidth: 400,

        sortable: false,

        render: (item: any) => (
          <OrderActionsDropdown
            item={item}
            onManage={() => onManage(item)}
            onViewNotes={() => onViewNotes(item)}
            onSendBOL={() => onSendBOL(item)}
            onOpenItem={(itemId) => onManage({ id: itemId })}
          />
        ),
      },
    ],

    [
      onManage,

      onViewNotes,

      onSendBOL,

      updateStatus,

      approveItem,

      renderDateCell,

      surchargeWeekForRow,

      stonePriceCentsForRow,
    ]
  );

  return { columns };
}
